import React, { useState, useEffect, useCallback } from "react";
import {
  getOutstandingReportApi,
  sendOutstandingReportNowApi,
  getOutstandingReportScheduleApi,
  saveOutstandingReportScheduleApi,
  deleteOutstandingReportScheduleApi,
} from "./accounts-api";
import { sanitizeInput } from "../common/security-utils";

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ORDINALS = ["First", "Second", "Third", "Fourth", "Last"];
const TABS = ["Preview", "Send Now", "Schedule"];

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const OutstandingReportModal = ({ accountId, onClose, notify, initialTab = "Preview" }) => {
  const [activeTab, setActiveTab] = useState(initialTab || "Preview");

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [loadingReport, setLoadingReport] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [sending, setSending] = useState(false);

  // Recipient selection — global to the whole modal (used by Preview, Send
  // Now, and Schedule alike), populated from contacts mapped to this account.
  const [toEmail, setToEmail] = useState("");
  const [ccEmails, setCcEmails] = useState([]);

  // Repeat schedule state
  const [scheduleId, setScheduleId] = useState(null);
  const [frequency, setFrequency] = useState("daily");
  const [interval, setInterval] = useState(1);
  const [weekdaysOnly, setWeekdaysOnly] = useState(false);
  const [weekdays, setWeekdays] = useState([]);
  const [monthlyMode, setMonthlyMode] = useState("on_day");
  const [monthlyDay, setMonthlyDay] = useState(1);
  const [monthlyOrdinal, setMonthlyOrdinal] = useState("First");
  const [monthlyWeekday, setMonthlyWeekday] = useState(0);
  const [startDate, setStartDate] = useState(formatDateInput(new Date()));
  const [endDate, setEndDate] = useState("");
  const [sendTime, setSendTime] = useState("09:00");
  const [savingSchedule, setSavingSchedule] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!accountId) return;
    setLoadingReport(true);
    try {
      const response = await getOutstandingReportApi(accountId);
      if (response?.data?.success) {
        const data = response.data.data;
        setReportData(data);
        // Default To: the account's own email if it's among the contact
        // emails or no contacts exist yet; otherwise the first contact.
        setToEmail((prev) => prev || data?.customer_email || data?.contacts?.[0]?.email || "");
      }
    } catch (error) {
      console.error("Error fetching outstanding report:", error);
    } finally {
      setLoadingReport(false);
    }
  }, [accountId]);

  const fetchSchedule = useCallback(async () => {
    if (!accountId) return;
    try {
      const response = await getOutstandingReportScheduleApi(accountId);
      const schedule = response?.data?.schedule;
      if (response?.data?.success && schedule) {
        setScheduleId(schedule.id);
        setFrequency(schedule.frequency || "daily");
        setInterval(schedule.interval || 1);
        setWeekdaysOnly(!!schedule.weekdays_only);
        setWeekdays(Array.isArray(schedule.weekdays) ? schedule.weekdays : []);
        setMonthlyMode(schedule.monthly_mode || "on_day");
        setMonthlyDay(schedule.monthly_day || 1);
        setMonthlyOrdinal(schedule.monthly_week_ordinal || "First");
        if (schedule.monthly_mode === "on_weekday" && Array.isArray(schedule.weekdays) && schedule.weekdays.length > 0) {
          setMonthlyWeekday(schedule.weekdays[0]);
        }
        setStartDate(schedule.start_date || formatDateInput(new Date()));
        setEndDate(schedule.end_date || "");
        setSendTime(schedule.send_time ? schedule.send_time.slice(0, 5) : "09:00");
        if (schedule.to_email) setToEmail(schedule.to_email);
        setCcEmails(Array.isArray(schedule.cc_emails) ? schedule.cc_emails : []);
      }
    } catch (error) {
      console.error("Error fetching outstanding report schedule:", error);
    }
  }, [accountId]);

  useEffect(() => {
    fetchReport();
    fetchSchedule();
  }, [fetchReport, fetchSchedule]);

  // Keep CC clean of whatever is currently selected as To — a contact
  // checked as CC can end up being picked as To afterward (or vice versa),
  // and without this the same address would be sent as both recipient and CC.
  useEffect(() => {
    if (!toEmail) return;
    setCcEmails((prev) => (prev.includes(toEmail) ? prev.filter((e) => e !== toEmail) : prev));
  }, [toEmail]);

  const handleSendNow = async () => {
    if (!toEmail) {
      notify?.("Please select a recipient email", false);
      return;
    }
    setSending(true);
    try {
      // TEMPORARY: merge CC into the To recipients at the API-call boundary
      // only — UI state (toEmail/ccEmails) is untouched, this just reshapes
      // the outgoing payload for Send Now specifically.
      const mergedToEmail = [toEmail, ...ccEmails].filter(Boolean).join(",");
      const response = await sendOutstandingReportNowApi(accountId, {
        to_email: mergedToEmail,
        cc_emails: [],
      });
      if (response?.data?.success) {
        notify?.(response.data.message || "Outstanding report sent successfully", true);
      } else {
        notify?.(response?.data?.message || "Failed to send report", false);
      }
    } catch (error) {
      notify?.(error?.response?.data?.message || "Failed to send report", false);
    } finally {
      setSending(false);
    }
  };

  const toggleWeekday = (dayIndex) => {
    setWeekdays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex].sort(),
    );
  };

  const handleSaveSchedule = async () => {
    if (frequency === "weekly" && weekdays.length === 0) {
      notify?.("Please select at least one day of the week", false);
      return;
    }
    if (!toEmail) {
      notify?.("Please select a recipient email", false);
      return;
    }

    const payload = {
      frequency,
      interval,
      start_date: startDate,
      end_date: endDate || null,
      send_time: sendTime,
      to_email: toEmail,
      cc_emails: ccEmails,
    };

    if (frequency === "daily") {
      payload.weekdays_only = weekdaysOnly;
    } else if (frequency === "weekly") {
      payload.weekdays = weekdays;
    } else if (frequency === "monthly") {
      payload.monthly_mode = monthlyMode;
      if (monthlyMode === "on_day") {
        payload.monthly_day = monthlyDay;
      } else {
        payload.monthly_week_ordinal = monthlyOrdinal;
        payload.weekdays = [monthlyWeekday];
      }
    }

    setSavingSchedule(true);
    try {
      const response = await saveOutstandingReportScheduleApi(accountId, payload);
      if (response?.data?.success) {
        notify?.(response.data.message || "Schedule saved successfully", true);
        fetchSchedule();
      } else {
        notify?.(response?.data?.message || "Failed to save schedule", false);
      }
    } catch (error) {
      notify?.(error?.response?.data?.message || "Failed to save schedule", false);
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRemoveSchedule = async () => {
    try {
      const response = await deleteOutstandingReportScheduleApi(accountId);
      if (response?.data?.success) {
        notify?.("Schedule removed", true);
        setScheduleId(null);
      }
    } catch (error) {
      notify?.("Failed to remove schedule", false);
    }
  };

  const summarySentence = () => {
    const start = startDate ? new Date(startDate).toDateString() : "";
    const end = endDate ? new Date(endDate).toDateString() : "";
    const timeLabel = sendTime;

    if (frequency === "daily") {
      const suffix = weekdaysOnly ? " (week days only)" : "";
      return `The report will repeat every ${interval} day(s) once${suffix} from ${start}${end ? `, to ${end}` : ""}, at ${timeLabel}.`;
    }
    if (frequency === "weekly") {
      const dayNames = weekdays.map((d) => WEEKDAY_NAMES[d]).join(", ") || "no days selected";
      return `The report will repeat every ${interval} week(s) once on ${dayNames}, from ${start}${end ? `, to ${end}` : ""}, at ${timeLabel}.`;
    }
    if (monthlyMode === "on_day") {
      return `The report will repeat every ${interval} month(s) once on day ${monthlyDay}, from ${start}${end ? `, to ${end}` : ""}, at ${timeLabel}.`;
    }
    return `The report will repeat every ${interval} month(s) once on the ${monthlyOrdinal} ${WEEKDAY_NAMES[monthlyWeekday]}, from ${start}${end ? `, to ${end}` : ""}, at ${timeLabel}.`;
  };

  const RecipientPicker = () => (
    <div className="outstanding-report-recipients">
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <label style={{ width: "40px", fontWeight: 600, fontSize: "13px" }}>To</label>
        <select
          className="form-control form-select"
          value={toEmail}
          onChange={(e) => setToEmail(e.target.value)}
        >
          {!(reportData?.contacts || []).some((c) => c.email === reportData?.customer_email) &&
            reportData?.customer_email && (
              <option value={reportData.customer_email}>
                Account — {reportData.customer_email}
              </option>
            )}
          {(reportData?.contacts || []).map((c) => (
            <option key={c.id} value={c.email}>
              {c.name} — {c.email}
            </option>
          ))}
          {!reportData?.customer_email && (reportData?.contacts || []).length === 0 && (
            <option value="">No contact email available</option>
          )}
        </select>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
        <label style={{ width: "40px", fontWeight: 600, fontSize: "13px", marginTop: "8px" }}>Cc</label>
        <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {(reportData?.contacts || [])
            .filter((c) => c.email !== toEmail)
            .map((c) => {
              const checked = ccEmails.includes(c.email);
              return (
                <label key={c.id} className={`cc-contact-chip ${checked ? "active" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setCcEmails((prev) =>
                        checked ? prev.filter((e) => e !== c.email) : [...prev, c.email],
                      )
                    }
                    style={{ display: "none" }}
                  />
                  {c.name} — {c.email}
                </label>
              );
            })}
          {(reportData?.contacts || []).filter((c) => c.email !== toEmail).length === 0 && (
            <span style={{ fontSize: "12px", color: "#8790A1" }}>No other contact emails available.</span>
          )}
          {reportData?.account_owner_email && reportData.account_owner_email !== toEmail && (() => {
            const ownerEmail = reportData.account_owner_email;
            const checked = ccEmails.includes(ownerEmail);
            return (
              <label className={`cc-contact-chip ${checked ? "active" : ""}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setCcEmails((prev) =>
                      checked ? prev.filter((e) => e !== ownerEmail) : [...prev, ownerEmail],
                    )
                  }
                  style={{ display: "none" }}
                />
                Account Owner — {ownerEmail}
              </label>
            );
          })()}
        </div>
      </div>
    </div>
  );

  const renderPreviewTab = () => (
    <>
     {/*  <table className="custom-table">
        <thead>
          <tr>
            <th>Invoice No.</th>
            <th>Title</th>
            <th>Invoice Date</th>
            <th>Due Date</th>
            <th>Days Overdue</th>
            <th>Amount</th>
            <th>Tax</th>
            <th>Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {(reportData?.invoices || []).length === 0 && (
            <tr>
              <td colSpan="8" style={{ textAlign: "center", color: "#8790A1" }}>
                No outstanding invoices.
              </td>
            </tr>
          )}
          {(reportData?.invoices || []).map((inv, idx) => (
            <tr key={idx}>
              <td>{sanitizeInput(inv.invoice_number)}</td>
              <td>{sanitizeInput(inv.invoice_title) || "-"}</td>
              <td>{sanitizeInput(inv.invoice_date) || "-"}</td>
              <td>{sanitizeInput(inv.due_date)}</td>
              <td style={{ color: inv.days_overdue > 0 ? "#e53935" : "#172B4D" }}>
                {inv.days_overdue > 0 ? `${inv.days_overdue} days` : "-"}
              </td>
              <td>
                {inv.currency_symbol}
                {Number(inv.total_amount).toFixed(2)}
              </td>
              <td>
                {inv.currency_symbol}
                {Number(inv.tax_amount || 0).toFixed(2)}
              </td>
              <td>
                {inv.currency_symbol}
                {Number(inv.outstanding).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table> */}
      {/* <div style={{ display: "flex", justifyContent: "flex-end", gap: "24px", marginTop: "8px", fontWeight: 600 }}>
        <span>Total Tax: {Number(reportData?.total_tax || 0).toFixed(2)}</span>
        <span>Total Outstanding: {Number(reportData?.total_outstanding || 0).toFixed(2)}</span>
        <span style={{ color: "#e53935" }}>Total Overdue: {Number(reportData?.total_overdue || 0).toFixed(2)}</span>
      </div> */}

      <div className="outstanding-report-preview">
        <div className="outstanding-report-preview-meta">
          <div>
            <strong>To:</strong> {toEmail || "—"}
          </div>
          <div>
            <strong>Cc:</strong> {ccEmails.join(", ") || "—"}
          </div>
          <div>
            <strong>Subject:</strong> {reportData?.email_subject || "Outstanding Invoice Summary"}
          </div>
        </div>
        <div
          className="outstanding-report-preview-body"
          dangerouslySetInnerHTML={{ __html: reportData?.email_html || "" }}
        />
      </div>
    </>
  );

  const renderSendNowTab = () => (
    <div>
      <div className="outstanding-report-summary" style={{ marginBottom: "16px" }}>
        This will immediately email the outstanding report shown in the Preview tab to the recipients
        selected above.
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600, marginBottom: "16px" }}>
        <span>Total Outstanding: {Number(reportData?.total_outstanding || 0).toFixed(2)}</span>
        <span style={{ color: "#e53935" }}>Total Overdue: {Number(reportData?.total_overdue || 0).toFixed(2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn-send-now" onClick={handleSendNow} disabled={sending}>
          {sending ? "Sending..." : "Send Now"}
        </button>
      </div>
    </div>
  );

  const renderScheduleTab = () => (
    <div>
      <div className="outstanding-report-preview-meta" style={{ marginBottom: "16px" }}>
        <div>
          <strong>To:</strong> {toEmail || "—"}
        </div>
        <div>
          <strong>Cc:</strong> {ccEmails.join(", ") || "—"}
        </div>
        <div>
          <strong>Subject:</strong> {reportData?.email_subject || "Outstanding Invoice Summary"}
        </div>
      </div>

      <select
        className="form-control form-select"
        value={frequency}
        onChange={(e) => setFrequency(e.target.value)}
        style={{ marginBottom: "16px" }}
      >
        <option value="daily">Repeat daily</option>
        <option value="weekly">Repeat weekly</option>
        <option value="monthly">Repeat monthly</option>
      </select>

      {frequency === "daily" && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span>Every</span>
            <input
              type="number"
              min="1"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value) || 1)}
              style={{ width: "50px" }}
              className="form-control"
            />
            <span>day(s) once</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={weekdaysOnly}
              onChange={(e) => setWeekdaysOnly(e.target.checked)}
            />
            Week days only
          </label>
        </div>
      )}

      {frequency === "weekly" && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <span>Every</span>
            <input
              type="number"
              min="1"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value) || 1)}
              style={{ width: "50px" }}
              className="form-control"
            />
            <span>week(s) once</span>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {WEEKDAY_LABELS.map((label, idx) => (
              <div
                key={idx}
                onClick={() => toggleWeekday(idx)}
                className={`weekday-pill ${weekdays.includes(idx) ? "active" : ""}`}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      )}

      {frequency === "monthly" && (
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
            <span>Every</span>
            <input
              type="number"
              min="1"
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value) || 1)}
              style={{ width: "50px" }}
              className="form-control"
            />
            <span>month(s) once</span>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", cursor: "pointer" }}>
            <input
              type="radio"
              checked={monthlyMode === "on_day"}
              onChange={() => setMonthlyMode("on_day")}
            />
            On day
            <input
              type="number"
              min="1"
              max="31"
              value={monthlyDay}
              onChange={(e) => setMonthlyDay(Number(e.target.value) || 1)}
              style={{ width: "50px" }}
              className="form-control"
              disabled={monthlyMode !== "on_day"}
            />
            day(s) once
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
            <input
              type="radio"
              checked={monthlyMode === "on_weekday"}
              onChange={() => setMonthlyMode("on_weekday")}
            />
            On the
            <select
              className="form-control form-select"
              value={monthlyOrdinal}
              onChange={(e) => setMonthlyOrdinal(e.target.value)}
              disabled={monthlyMode !== "on_weekday"}
              style={{ width: "110px" }}
            >
              {ORDINALS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <select
              className="form-control form-select"
              value={monthlyWeekday}
              onChange={(e) => setMonthlyWeekday(Number(e.target.value))}
              disabled={monthlyMode !== "on_weekday"}
              style={{ width: "130px" }}
            >
              {WEEKDAY_NAMES.map((name, idx) => (
                <option key={name} value={idx}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div style={{ fontWeight: 600, marginBottom: "8px" }}>Date</div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <input
          type="date"
          className="form-control"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <span>-</span>
        <input
          type="date"
          className="form-control"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div style={{ fontWeight: 600, marginBottom: "8px" }}>Time</div>
      <input
        type="time"
        className="form-control"
        value={sendTime}
        onChange={(e) => setSendTime(e.target.value)}
        style={{ marginBottom: "16px", maxWidth: "160px" }}
      />

      <div className="outstanding-report-summary">{summarySentence()}</div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px" }}>
        {scheduleId && (
          <button className="btn-remove-schedule" onClick={handleRemoveSchedule}>
            Remove Schedule
          </button>
        )}
        <button className="btn-save-schedule" onClick={handleSaveSchedule} disabled={savingSchedule}>
          {savingSchedule ? "Saving..." : "Save Schedule"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="outstanding-report-overlay">
      <div className="outstanding-report-modal">
        <div className="outstanding-report-header">
          <h5 style={{ margin: 0, fontWeight: 700 }}>Outstanding Report</h5>
          <span onClick={onClose} style={{ cursor: "pointer", fontSize: "30px" }}>
            ×
          </span>
        </div>

        {loadingReport ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>Loading...</div>
        ) : (
          <>
            <div className="outstanding-report-body" style={{ paddingBottom: 0 }}>
              <RecipientPicker />
            </div>

            <div className="outstanding-report-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  className={`outstanding-report-tab ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="outstanding-report-body" style={{ paddingTop: "16px" }}>
              {activeTab === "Preview" && renderPreviewTab()}
              {activeTab === "Send Now" && renderSendNowTab()}
              {activeTab === "Schedule" && renderScheduleTab()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OutstandingReportModal;
